<template>
  <component v-show="loaded" :is="layout" class="content">
    <slot />
    <cookie-consent-modal></cookie-consent-modal>
  </component>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import tinycolor from "tinycolor2";

const defaultLayout = "AppLayoutDefault";

const importedLayouts = {};

export default {
  name: "AppLayout",
  components: {
    CallMeBackModal: () => import("@/components/CallMeBackModal.vue"),
    CallMeBackButton: () => import("@/components/CallMeBackButton.vue"),
    BasketButton: () => import("@/components/basket/BasketButton.vue"),
    BasketModal: () => import("@/components/basket/BasketModal.vue"),
    CookieConsentModal: () => import("@/components/CookieConsentModal.vue"),
  },
  data() {
    return {
      dealerBrandingVariables: {},
      branding: {
        brandingPrimary: "yellow",
        brandingSecondary: "black",
        black: "black",
        systemSuccess: "green",
        systemWarning: "gold",
        systemError: "red",
      },
      loaded: false,
    };
  },
  computed: {
    ...mapGetters({
      loggedIn: "session/isReady",
      hasActivePurchase: "purchase/hasActivePurchase",
      getDealerBranding: "leasing/getDealerBranding",
      dealerId: "leasing/dealerId",
    }),
    basketVisible() {
      return (
        this.loggedIn &&
        this.hasActivePurchase &&
        !this.$route.fullPath.includes("/checkout")
      );
    },
    layout() {
      const layout = this.$route.meta.layout || defaultLayout;

      // Avoid unnecessary re-render of layouts (dynamic component in the template)
      if (!importedLayouts[layout]) {
        importedLayouts[layout] = () => import(`@/layouts/${layout}.vue`);
      }
      return importedLayouts[layout];
    },
  },
  methods: {
    ...mapActions({
      fetchDealerBranding: "leasing/fetchDealerBranding",
    }),
    darken(color, amount) {
      return tinycolor.mix(color, "black", amount).toHexString();
    },
    lighten(color, amount) {
      return tinycolor.mix("white", color, amount).toHexString();
    },
  },
  async created() {
    if (!!this.dealerId) {
      await this.fetchDealerBranding(this.dealerId);
    }
    this.branding = this.getDealerBranding;
    document.body.classList.add("dealer-branding-wrapper");
    this.dealerBrandingVariables = {
      "--compact-logo": `url('${this.branding.compactLogo}')`,
      "--big-logo": `url('${this.branding.bigLogo}')`,
      "--hero-image": `url('${this.branding.heroImage}')`,

      "--black": tinycolor(this.branding.black).toHexString(),
      "--gray-50": this.lighten(this.branding.black, 4),
      "--gray-200": this.lighten(this.branding.black, 16),
      "--gray-400": this.lighten(this.branding.black, 32),
      "--gray-600": this.lighten(this.branding.black, 48),
      "--gray-800": this.lighten(this.branding.black, 64),
      "--gray-1000": this.lighten(this.branding.black, 80),
      "--gray-1200": this.lighten(this.branding.black, 96),

      "--branding-primary": tinycolor(
        this.branding.brandingPrimary
      ).toHexString(),
      "--branding-primary-light": this.lighten(
        this.branding.brandingPrimary,
        25
      ),
      "--branding-primary-dark": this.darken(this.branding.brandingPrimary, 10),

      "--branding-secondary": tinycolor(
        this.branding.brandingSecondary
      ).toHexString(),
      "--branding-secondary-light": this.lighten(
        this.branding.brandingSecondary,
        80
      ),
      "--branding-secondary-dark": this.darken(
        this.branding.brandingSecondary,
        20
      ),

      "--system-success": tinycolor(this.branding.systemSuccess).toHexString(),
      "--system-success-light": this.lighten(this.branding.systemSuccess, 20),
      "--system-success-dark": this.darken(this.branding.systemSuccess, 20),

      "--system-warning": tinycolor(this.branding.systemWarning).toHexString(),
      "--system-warning-light": this.lighten(this.branding.systemWarning, 20),
      "--system-warning-dark": this.darken(this.branding.systemWarning, 20),

      "--system-error": tinycolor(this.branding.systemError).toHexString(),
      "--system-error-light": this.lighten(this.branding.systemError, 20),
      "--system-error-dark": this.darken(this.branding.systemError, 20),

      "--button-primary-font-color": this.branding.buttonPrimaryFontColor,
      "--button-secondary-font-color": this.branding.buttonSecondaryFontColor,
      "--card-border-radius": this.branding.cardBorderRadius,
      "--button-border-radius": this.branding.buttonBorderRadius,
      "--bulma-progress-value-background-color": tinycolor(
        this.branding.brandingPrimary
      ).toHexString(),
    };
    document.body.style = Object.entries(this.dealerBrandingVariables).reduce(
      (previousValue, currentValue) => {
        return `${previousValue} ${currentValue[0]}: ${currentValue[1]};`;
      },
      ""
    );
    this.loaded = true;
  },
};
</script>

<style lang="scss">
@use "../assets/scss/dealer.scss";
</style>
